/**
 * @file: MessageEmojiGridPanel.tsx
 *
 * The expanded reaction picker — a scrollable grid of common emoji shown when
 * the reaction pill's chevron is tapped. A curated set (no search / full
 * keyboard); tapping an emoji toggles it on the message. Pure: the overlay
 * supplies the size, the toggle handler, and which emojis are already applied.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

/** Curated common reaction emoji (most-used first). */
export const COMMON_EMOJIS = [
  '👍',
  '👎',
  '❤️',
  '🔥',
  '🥰',
  '👏',
  '😄',
  '😂',
  '🤣',
  '😊',
  '😍',
  '🤔',
  '😢',
  '😭',
  '😅',
  '😆',
  '😉',
  '😋',
  '😎',
  '😘',
  '🥺',
  '😳',
  '😱',
  '😡',
  '🤬',
  '🤯',
  '😴',
  '🙄',
  '😬',
  '🤐',
  '😇',
  '🤗',
  '🤓',
  '🥳',
  '🥹',
  '😤',
  '😩',
  '🙏',
  '💪',
  '👌',
  '✌️',
  '🤝',
  '👋',
  '🙌',
  '👀',
  '💯',
  '✨',
  '⭐',
  '🎉',
  '🎊',
  '💔',
  '💕',
  '💖',
  '🍻',
  '🌹',
  '💩',
  '👻',
  '🤡',
  '💀',
  '🎃',
  '🐶',
  '🐱',
  '🌈',
  '☀️',
];

export const MessageEmojiGridPanel: FC<{
  width: number;
  height: number;
  myReacted: Set<string>;
  onReact: (emoji: string) => void;
}> = memo(({ width, height, myReacted, onReact }) => {
  const { rpx } = useResponsiveContext();
  // Stretch the cells to fill the row exactly: pick the column count that fits
  // at a ~44px target, then divide the content width evenly so there's no
  // leftover gap on the right.
  const padH = rpx(8);
  const innerWidth = width - padH * 2;
  const columns = Math.max(1, Math.floor(innerWidth / rpx(44)));
  const cellW = innerWidth / columns;
  const cellH = rpx(44);
  return (
    <View
      style={{
        width,
        height,
        backgroundColor: '#ffffff',
        borderRadius: rpx(18),
        paddingVertical: rpx(6),
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
        elevation: 8,
        overflow: 'hidden',
      }}
    >
      <ScrollView
        contentContainerStyle={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          paddingHorizontal: padH,
          paddingVertical: rpx(4),
        }}
        showsVerticalScrollIndicator={false}
      >
        {COMMON_EMOJIS.map(emoji => (
          <TouchableOpacity
            key={emoji}
            activeOpacity={0.6}
            onPress={() => onReact(emoji)}
            style={{
              width: cellW,
              height: cellH,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: cellH / 2,
              backgroundColor: myReacted.has(emoji) ? '#dbeafe' : 'transparent',
            }}
          >
            <Typography.Text size={rpx(26)} lineHeight={rpx(34)}>
              {emoji}
            </Typography.Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
});
